import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCustomersYearly } from "@/lib/hooks";
import { fmtInt, fmtUSDCompact, fmtDelta, deltaTone } from "@/lib/format";
import { downloadXlsx, stampedName } from "@/lib/exportXlsx";
import DownloadButton from "@/components/DownloadButton";
import type { YearlyAccount } from "@/lib/api";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

type Metric = "revenue" | "qty";
type ProductKey = "all" | "catheters";
type SortDir = "asc" | "desc";

export default function YearlyOverview() {
  const [metric, setMetric] = useState<Metric>("revenue");
  const [productKey, setProductKey] = useState<ProductKey>("all");
  const [sortKey, setSortKey] = useState<string>("__current"); // year string | "name" | "yoy" | "__current"
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const productGroups = productKey === "catheters" ? ["UOT", "IAP", "SmartFoley Adapter"] : [];
  const { data, isLoading, error } = useCustomersYearly(productGroups);

  const fmt = metric === "revenue" ? fmtUSDCompact : fmtInt;
  const years = data?.years ?? [];
  const currentYear = data?.current_year ?? null;
  const effectiveSortKey = sortKey === "__current" ? String(currentYear ?? "") : sortKey;

  const valFor = (c: YearlyAccount, year: number) =>
    (metric === "revenue" ? c.revenue : c.qty)[String(year)] ?? 0;
  const ytdCur = (c: YearlyAccount) =>
    metric === "revenue" ? c.ytd_current.revenue : c.ytd_current.qty;
  const ytdPrior = (c: YearlyAccount) =>
    metric === "revenue" ? c.ytd_prior.revenue : c.ytd_prior.qty;
  const yoyPct = (c: YearlyAccount): number | null => {
    const p = ytdPrior(c);
    return p > 0 ? ((ytdCur(c) - p) / p) * 100 : null;
  };

  const rows = useMemo(() => {
    const list = [...(data?.customers ?? [])];
    list.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      if (effectiveSortKey === "name") {
        av = a.customer_name.toLowerCase();
        bv = b.customer_name.toLowerCase();
      } else if (effectiveSortKey === "yoy") {
        av = yoyPct(a) ?? -Infinity;
        bv = yoyPct(b) ?? -Infinity;
      } else if (effectiveSortKey === "monitors") {
        av = a.monitor_count ?? 0;
        bv = b.monitor_count ?? 0;
      } else {
        av = valFor(a, Number(effectiveSortKey));
        bv = valFor(b, Number(effectiveSortKey));
      }
      const cmp = typeof av === "string" ? av.localeCompare(bv as string) : av - (bv as number);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, metric, effectiveSortKey, sortDir]);

  const totals = useMemo(() => {
    const perYear: Record<string, number> = {};
    let curYtd = 0;
    let priorYtd = 0;
    let totalMonitors = 0;
    for (const c of data?.customers ?? []) {
      for (const y of years) perYear[y] = (perYear[y] ?? 0) + valFor(c, y);
      curYtd += ytdCur(c);
      priorYtd += ytdPrior(c);
      totalMonitors += c.monitor_count ?? 0;
    }
    const yoy = priorYtd > 0 ? ((curYtd - priorYtd) / priorYtd) * 100 : null;
    return { perYear, yoy, totalMonitors };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, metric, years]);

  const setSort = (key: string) => {
    if (key === sortKey || (key === String(currentYear) && sortKey === "__current")) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const arrow = (key: string) =>
    effectiveSortKey === key || (key === "__current" && sortKey === "__current")
      ? sortDir === "asc"
        ? " ↑"
        : " ↓"
      : "";

  const ytdLabel =
    data?.ytd_through_month != null ? `Jan–${MONTHS[data.ytd_through_month - 1]}` : "YTD";

  function exportCsv() {
    const headers = [
      "Account",
      "Monitors",
      ...years.map((y) => `${y}${y === currentYear ? " (YTD)" : ""} ${metric}`),
      `YoY % (${ytdLabel})`,
    ];
    const body = rows.map((c) => [
      c.customer_name,
      c.monitor_count ?? 0,
      ...years.map((y) => valFor(c, y)),
      yoyPct(c) ?? "",
    ]);
    body.push([
      "All accounts",
      totals.totalMonitors,
      ...years.map((y) => totals.perYear[y] ?? 0),
      totals.yoy ?? "",
    ]);
    downloadXlsx(stampedName(`year-over-year-${metric}`), headers, body);
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Year over year</h1>
          <p className="mt-1 text-sm text-ink-500">
            Annual sales by hospital account. The current year is in progress; the YoY column
            compares {ytdLabel} this year vs. the same months last year.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Toggle
            value={productKey}
            onChange={(v) => setProductKey(v as ProductKey)}
            options={[
              { value: "all", label: "All products" },
              { value: "catheters", label: "Catheters" },
            ]}
          />
          <Toggle
            value={metric}
            onChange={(v) => setMetric(v as Metric)}
            options={[
              { value: "revenue", label: "Revenue" },
              { value: "qty", label: "Quantity" },
            ]}
          />
          <DownloadButton onClick={exportCsv} disabled={!data?.customers.length} />
        </div>
      </header>

      {error && (
        <div className="card mb-6 p-4 text-sm text-danger">{(error as Error).message}</div>
      )}

      <section className="card overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-10 text-center text-sm text-ink-500">Loading…</div>
          ) : (data?.customers.length ?? 0) === 0 ? (
            <div className="p-10 text-center text-sm text-ink-500">No account sales history.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <Th onClick={() => setSort("name")} align="left">
                    Account{arrow("name")}
                  </Th>
                  <Th onClick={() => setSort("monitors")} align="right">
                    Monitors{arrow("monitors")}
                  </Th>
                  {years.map((y) => (
                    <Th key={y} onClick={() => setSort(String(y))} align="right">
                      {y}
                      {y === currentYear ? " (YTD)" : ""}
                      {arrow(String(y))}
                    </Th>
                  ))}
                  <Th onClick={() => setSort("yoy")} align="right">
                    YoY · {ytdLabel}
                    {arrow("yoy")}
                  </Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {rows.map((c) => {
                  const pct = yoyPct(c);
                  const tone = deltaTone(pct);
                  return (
                    <tr key={c.customer_id} className="hover:bg-ink-50">
                      <td className="px-5 py-2 font-medium text-ink-900">
                        <Link
                          to={`/customers/${encodeURIComponent(c.customer_id)}`}
                          className="hover:text-accent-600"
                        >
                          {c.customer_name}
                        </Link>
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums text-ink-700">
                        {c.monitor_count || "—"}
                      </td>
                      {years.map((y) => {
                        const v = valFor(c, y);
                        return (
                          <td
                            key={y}
                            className={`px-5 py-2 text-right tabular-nums ${
                              v === 0 ? "text-ink-300" : "text-ink-900"
                            }`}
                          >
                            {v === 0 ? "—" : fmt(v)}
                          </td>
                        );
                      })}
                      <td className="px-5 py-2 text-right tabular-nums">
                        <span
                          className={
                            tone === "up" ? "text-success" : tone === "down" ? "text-danger" : "text-ink-500"
                          }
                        >
                          {fmtDelta(pct)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-ink-300 bg-ink-50 font-semibold">
                  <td className="px-5 py-2 text-ink-900">All accounts</td>
                  <td className="px-5 py-2 text-right tabular-nums text-ink-900">
                    {totals.totalMonitors}
                  </td>
                  {years.map((y) => (
                    <td key={y} className="px-5 py-2 text-right tabular-nums text-ink-900">
                      {fmt(totals.perYear[y] ?? 0)}
                    </td>
                  ))}
                  <td className="px-5 py-2 text-right tabular-nums">
                    <span
                      className={
                        deltaTone(totals.yoy) === "up"
                          ? "text-success"
                          : deltaTone(totals.yoy) === "down"
                            ? "text-danger"
                            : "text-ink-500"
                      }
                    >
                      {fmtDelta(totals.yoy)}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </section>

      {data && (
        <p className="mt-3 text-xs text-ink-500">
          {data.customers.length} accounts · all products{productKey === "catheters" ? " (catheters only)" : ""}.
          Year columns are full-year totals; {currentYear} is year-to-date.
        </p>
      )}
    </div>
  );
}

function Th({
  children,
  onClick,
  align,
}: {
  children: React.ReactNode;
  onClick: () => void;
  align: "left" | "right";
}) {
  return (
    <th className={`px-5 py-2 ${align === "left" ? "text-left" : "text-right"}`}>
      <button
        type="button"
        onClick={onClick}
        className="font-medium uppercase tracking-wide text-ink-500 hover:text-ink-900"
      >
        {children}
      </button>
    </th>
  );
}

function Toggle({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="flex gap-1.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
            value === o.value
              ? "border-ink-900 bg-ink-900 text-white"
              : "border-ink-200 bg-white text-ink-600 hover:border-ink-300"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
